export const CUSTOM_DESIGN_ID = 99;

const DEFAULT_CUSTOM_DESIGN = {
  baseDesign: 5,
  layout: 'boutique',
  fontPreset: 'moderna',
  heroStyle: 'boxed',
  cardStyle: 'soft',
  headerStyle: 'glass',
  menuStyle: 'dropdown',
  footerStyle: 'complete',
  density: 'balanced',
  radius: 'soft',
  productImage: 'contain',
  colors: {
    background: '',
    surface: '',
    text: '',
    accent: '',
    secondary: '',
  },
  customHtml: {
    enabled: false,
    mode: 'sections',
    fullHome: '',
    beforeHero: '',
    afterHero: '',
    beforeProducts: '',
    beforeFooter: '',
    customCss: '',
  },
};

const FONT_MAP = {
  moderna: {
    body: '"Manrope", "Segoe UI", Arial, sans-serif',
    heading: '"Manrope", "Segoe UI", Arial, sans-serif',
  },
  editorial: {
    body: '"Manrope", "Segoe UI", Arial, sans-serif',
    heading: '"Fraunces", Georgia, serif',
  },
  elegante: {
    body: '"Manrope", "Segoe UI", Arial, sans-serif',
    heading: '"Playfair Display", Georgia, serif',
  },
  tech: {
    body: '"IBM Plex Mono", Consolas, monospace',
    heading: '"Space Grotesk", "Segoe UI", Arial, sans-serif',
  },
  playful: {
    body: '"Nunito", "Segoe UI", Arial, sans-serif',
    heading: '"Nunito", "Segoe UI", Arial, sans-serif',
  },
  impacto: {
    body: '"Manrope", "Segoe UI", Arial, sans-serif',
    heading: '"Bebas Neue", Impact, sans-serif',
  },
};

const safeToken = (value, fallback) => (
  String(value || fallback).replace(/[^a-z0-9-]/gi, '').toLowerCase()
);

const isHex = (value) => /^#[0-9a-fA-F]{6}$/.test(String(value || '').trim());

export const isCustomDesign = (clientOrDesign) => (
  Number(typeof clientOrDesign === 'object' ? clientOrDesign?.design : clientOrDesign) === CUSTOM_DESIGN_ID
);

export const getCustomDesignSettings = (client) => ({
  ...DEFAULT_CUSTOM_DESIGN,
  ...(client?.custom_design_settings || {}),
  colors: {
    ...DEFAULT_CUSTOM_DESIGN.colors,
    ...(client?.custom_design_settings?.colors || {}),
  },
  customHtml: {
    ...DEFAULT_CUSTOM_DESIGN.customHtml,
    ...(client?.custom_design_settings?.customHtml || {}),
  },
});

export const getCustomDesignClasses = (client) => {
  if (!isCustomDesign(client)) return '';

  const settings = getCustomDesignSettings(client);
  return [
    'design-custom',
    `custom-layout-${safeToken(settings.layout, 'boutique')}`,
    `custom-font-${safeToken(settings.fontPreset, 'moderna')}`,
    `custom-hero-${safeToken(settings.heroStyle, 'boxed')}`,
    `custom-card-${safeToken(settings.cardStyle, 'soft')}`,
    `custom-header-${safeToken(settings.headerStyle, 'glass')}`,
    `custom-menu-${safeToken(settings.menuStyle, 'dropdown')}`,
    `custom-footer-${safeToken(settings.footerStyle, 'complete')}`,
    `custom-density-${safeToken(settings.density, 'balanced')}`,
    `custom-radius-${safeToken(settings.radius, 'soft')}`,
    `custom-image-${safeToken(settings.productImage, 'contain')}`,
  ].join(' ');
};

export const getCustomDesignStyle = (client) => {
  if (!isCustomDesign(client)) return {};

  const settings = getCustomDesignSettings(client);
  const colors = settings.colors || {};
  const fonts = FONT_MAP[settings.fontPreset] || FONT_MAP.moderna;
  const style = {
    '--custom-font-body': fonts.body,
    '--custom-font-heading': fonts.heading,
  };

  if (isHex(colors.background)) style['--custom-bg'] = colors.background;
  if (isHex(colors.surface)) style['--custom-surface'] = colors.surface;
  if (isHex(colors.text)) style['--custom-text'] = colors.text;
  if (isHex(colors.accent)) style['--custom-accent'] = colors.accent;
  if (isHex(colors.secondary)) style['--custom-secondary'] = colors.secondary;

  return style;
};
